/** Half-up to 2 decimal places (aligns with typical invoice / BTW totals). */
export function roundMoneyToCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Dutch locale: comma decimals, e.g. € 19,95 */
export function formatEuroNl(value) {
  const n = Number(value);
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number.isFinite(n) ? n : 0);
}

export function setCookie(name, value, options = {}) {
  const {
    days = 7,
    path = '/',
    secure = window.location.protocol === 'https:',
    sameSite = 'Lax',
  } = options;

  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000
  ).toUTCString();
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Expires=${expires}; Path=${path}; SameSite=${sameSite}`;
  if (secure) cookie += '; Secure';
  document.cookie = cookie;
}

export function getCookie(name) {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (let i = 0; i < cookies.length; i++) {
    const parts = cookies[i].split('=');
    const key = decodeURIComponent(parts[0]);
    if (key === name) {
      try {
        return decodeURIComponent(parts.slice(1).join('='));
      } catch (e) {
        return parts.slice(1).join('=');
      }
    }
  }
  return null;
}

export function deleteCookie(name, { path = '/' } = {}) {
  document.cookie = `${encodeURIComponent(name)}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=${path}`;
}

/** Scroll to the storefront footer without changing the URL hash (avoids mobile history conflicts). */
export function scrollToFooter() {
  document.getElementById('footer')?.scrollIntoView({ behavior: 'smooth' });
}

/** Convert HTML string to plain text for short previews. */
export function htmlToPlainText(html) {
  const raw = String(html || '').trim();
  if (!raw) return '';
  if (typeof document === 'undefined') {
    return raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const div = document.createElement('div');
  div.innerHTML = raw;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

const WEB_INFO_BODY_START =
  /\b(In de natuur|Dit product|Dit supplement|Het product|Deze formule|This product|The product)\b/i;

function isTitleLikeText(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return t.length > 0 && t.length < 100;
}

/** Strip leading title text/nodes from a container (handles <p>Title<br><br>Body</p> etc.). */
function stripLeadingTitleNodes(container) {
  if (!container) return;

  let changed = true;
  while (changed) {
    changed = false;

    while (
      container.firstChild?.nodeType === 3 &&
      !container.firstChild.textContent.trim()
    ) {
      container.removeChild(container.firstChild);
      changed = true;
    }

    if (container.firstChild?.nodeType === 3) {
      const leadingText = container.firstChild.textContent.trim();
      if (isTitleLikeText(leadingText)) {
        container.removeChild(container.firstChild);
        changed = true;
        continue;
      }
    }

    const firstEl = container.firstElementChild;
    if (!firstEl) break;

    const tag = firstEl.tagName.toLowerCase();
    if (tag === 'br') {
      firstEl.remove();
      changed = true;
      continue;
    }
    if ((tag === 'strong' || tag === 'b') && isTitleLikeText(firstEl.textContent)) {
      firstEl.remove();
      changed = true;
      continue;
    }
    if (/^h[1-6]$/.test(tag) && isTitleLikeText(firstEl.textContent)) {
      firstEl.remove();
      changed = true;
      continue;
    }
    break;
  }

  const fullText = (container.textContent || '').replace(/\s+/g, ' ').trim();
  const bodyMatch = fullText.match(WEB_INFO_BODY_START);
  if (bodyMatch && bodyMatch.index > 0 && bodyMatch.index <= 120) {
    const bodyText = fullText.slice(bodyMatch.index).trim();
    if (bodyText) {
      container.textContent = bodyText;
    }
  } else if (isTitleLikeText(fullText) && !bodyMatch) {
    container.textContent = '';
  }
}

/** Remove leading product-title blocks from web_info HTML (title is shown outside). */
export function stripWebInfoTitle(html) {
  const raw = String(html || '').trim();
  if (!raw) return '';
  if (typeof document === 'undefined') return raw;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = raw;

  stripLeadingTitleNodes(wrapper);

  while (wrapper.firstElementChild) {
    const el = wrapper.firstElementChild;
    const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    const tag = el.tagName.toLowerCase();
    const isHeading = /^h[1-6]$/.test(tag);
    const isShortLeadBlock =
      (tag === 'p' || tag === 'div') && text.length > 0 && text.length < 100;
    if (isHeading || isShortLeadBlock) {
      wrapper.removeChild(el);
    } else {
      break;
    }
  }

  if (wrapper.firstElementChild) {
    stripLeadingTitleNodes(wrapper.firstElementChild);
    const remaining = (wrapper.firstElementChild.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!remaining) {
      wrapper.removeChild(wrapper.firstElementChild);
    }
  }

  stripLeadingTitleNodes(wrapper);

  const cleaned = wrapper.innerHTML.trim();
  const cleanedText = htmlToPlainText(cleaned);
  if (!cleanedText) return '';

  return cleaned;
}

/** Plain-text body of web_info with titles removed (for short previews). */
export function getWebInfoBodyText(html) {
  const stripped = stripWebInfoTitle(html);
  let text = htmlToPlainText(stripped);
  if (!text) return '';

  const bodyMatch = text.match(WEB_INFO_BODY_START);
  if (bodyMatch && bodyMatch.index > 0 && bodyMatch.index <= 80) {
    text = text.slice(bodyMatch.index).trim();
  }

  return text;
}
